import { useState, useEffect } from 'react';
import { TrendingDown, TrendingUp, Wallet, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const TRY = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);

const TX_TYPE_BADGE = {
  'Borç':      'rose',
  'Tahsilat':  'success',
  'Ödeme':     'warning',
  'Masraf':    'destructive',
  'Avans':     'purple',
  'Komisyon':  'cyan',
};

const DATE_FMT = (d) =>
  new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

export default function Dashboard() {
  const { toast } = useToast();

  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fetchStats = async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const { data } = await api.get('/dashboard/summary');
      setStats(data);
    } catch {
      toast({ title: 'Hata', description: 'Veriler yüklenirken hata oluştu.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchStats(); }, []); // eslint-disable-line

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const net = (stats?.net_balance ?? 0);
  const netPositive = net >= 0;

  return (
    <div className="space-y-6 relative">
      {/* Arka plan halo efekti */}
      <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-20 right-0 w-48 h-48 bg-emerald-600/5 rounded-full blur-3xl pointer-events-none" />

      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Finansal Özet</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchStats(true)}
            disabled={isRefreshing}
            className="text-slate-400"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Toplam Borç */}
        <Card className="border-rose-900/30 bg-rose-950/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription className="text-rose-400/80 text-xs uppercase tracking-wider">Toplam Borç</CardDescription>
              <div className="w-8 h-8 rounded-lg bg-rose-600/15 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-rose-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-rose-300">{TRY(stats?.total_borc ?? 0)}</p>
            <p className="text-xs text-slate-500 mt-1">{stats?.cari_count ?? 0} cari kayıt</p>
          </CardContent>
        </Card>

        {/* Toplam Alacak */}
        <Card className="border-emerald-900/30 bg-emerald-950/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription className="text-emerald-400/80 text-xs uppercase tracking-wider">Toplam Alacak</CardDescription>
              <div className="w-8 h-8 rounded-lg bg-emerald-600/15 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-300">{TRY(stats?.total_alacak ?? 0)}</p>
            <p className="text-xs text-slate-500 mt-1">{stats?.transaction_count ?? 0} işlem</p>
          </CardContent>
        </Card>

        {/* Net Bakiye */}
        <Card className={`border-${netPositive ? 'blue' : 'orange'}-900/30 bg-${netPositive ? 'blue' : 'orange'}-950/20 sm:col-span-2`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription className={`text-${netPositive ? 'blue' : 'orange'}-400/80 text-xs uppercase tracking-wider`}>
                Net Bakiye
              </CardDescription>
              <div className={`w-8 h-8 rounded-lg bg-${netPositive ? 'blue' : 'orange'}-600/15 flex items-center justify-center`}>
                <Wallet className={`w-4 h-4 text-${netPositive ? 'blue' : 'orange'}-400`} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold text-${netPositive ? 'blue' : 'orange'}-300`}>
              {netPositive ? '+' : ''}{TRY(net)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {netPositive ? 'Pozitif finansal durum' : 'Negatif finansal durum'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Son İşlemler */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Son İşlemler</CardTitle>
              <CardDescription>En güncel 5 finansal hareket</CardDescription>
            </div>
            <Link to="/transactions">
              <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 gap-1">
                Tümünü Gör <ArrowRight className="w-3 h-3" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.recent_transactions?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cari</TableHead>
                  <TableHead>İşlem Türü</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead className="text-right">Tarih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="font-medium text-slate-800 dark:text-slate-200">{tx.cari_name}</TableCell>
                    <TableCell>
                      <Badge variant={TX_TYPE_BADGE[tx.type] || 'secondary'}>{tx.type}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-800 dark:text-slate-200">{TRY(tx.amount)}</TableCell>
                    <TableCell className="text-right text-slate-500 dark:text-slate-400 text-xs">{DATE_FMT(tx.transaction_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Wallet className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Henüz işlem yok</p>
              <Link to="/transactions" className="mt-2">
                <Button variant="ghost" size="sm" className="text-blue-400">İlk işlemi ekle</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
